import React, { useMemo } from "react";
import { Drawer, Empty, Progress, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ThreeMPhaseNode,
  normalizeRollup,
} from "../ThreeMConsolidation/threeMShared";
import { fmtINR, fmtINRCompact } from "./machineryShared";
import { useWbsName } from "../../shared/useWbsCodes";

// Phase-level machinery cost drill-down: phases ranked by machinery cost
// (project-wide, from the unfiltered 3M consolidation tree the page already
// loads for its filter options — the rollup's machineryAmount already
// includes tools and consumables), each expandable to its activities ranked
// the same way. Shares: phase % of project machinery total, activity % of
// its phase.

interface PhaseRow {
  key: number;
  rank: number;
  name: string;
  cost: number;
  share: number; // % of project machinery total
  activities: ActivityRow[];
}

interface ActivityRow {
  key: number;
  rank: number;
  name: string;
  cost: number;
  share: number; // % of parent phase machinery cost
}

const costCell = (cost: number) => (
  <Tooltip title={fmtINR(cost)}>
    <span
      style={{
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        cursor: "default",
      }}
    >
      {fmtINRCompact(cost)}
    </span>
  </Tooltip>
);

const shareCell = (share: number) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <Progress
      percent={Math.min(100, Math.round(share * 10) / 10)}
      size="small"
      strokeWidth={4}
      showInfo={false}
      strokeColor="#2f54eb"
      style={{ flex: 1, minWidth: 60, margin: 0 }}
    />
    <span
      style={{
        fontSize: 12,
        color: "#595959",
        fontVariantNumeric: "tabular-nums",
        width: 42,
        textAlign: "right",
      }}
    >
      {share.toFixed(1)}%
    </span>
  </div>
);

const PhaseCostDrawer: React.FC<{
  open: boolean;
  phases: ThreeMPhaseNode[];
  onClose: () => void;
  // Render target override — the page passes its own container so the drawer
  // stays visible while the report is in browser fullscreen.
  getContainer?: () => HTMLElement;
}> = ({ open, phases, onClose, getContainer }) => {
  const wbsName = useWbsName();
  const rows = useMemo<PhaseRow[]>(() => {
    const withCost = phases.map((phase) => {
      const phaseCost = normalizeRollup(phase.rollup).machineryAmount;
      const activities = (phase.activities || [])
        .map((activity) => ({
          activityId: activity.activityId,
          name: activity.activityName,
          cost: normalizeRollup(activity.rollup).machineryAmount,
        }))
        .sort((a, b) => b.cost - a.cost)
        .map((activity, index) => ({
          key: activity.activityId,
          rank: index + 1,
          name: activity.name,
          cost: activity.cost,
          share: phaseCost > 0 ? (activity.cost / phaseCost) * 100 : 0,
        }));
      return { phase, phaseCost, activities };
    });
    const projectTotal = withCost.reduce((sum, p) => sum + p.phaseCost, 0);
    return withCost
      .sort((a, b) => b.phaseCost - a.phaseCost)
      .map((p, index) => ({
        key: p.phase.phaseId,
        rank: index + 1,
        name: p.phase.phaseName,
        cost: p.phaseCost,
        share: projectTotal > 0 ? (p.phaseCost / projectTotal) * 100 : 0,
        activities: p.activities,
      }));
  }, [phases]);

  const phaseColumns: ColumnsType<PhaseRow> = [
    {
      title: "#",
      dataIndex: "rank",
      width: 48,
      render: (rank: number) => (
        <span style={{ color: "#8c8c8c" }}>{rank}</span>
      ),
    },
    {
      title: "Phase",
      dataIndex: "name",
      render: (name: string, row) => (
        <>
          <div style={{ fontWeight: 500 }} title={name}>{wbsName(name)}</div>
          <div style={{ fontSize: 11, color: "#8c8c8c" }}>
            {row.activities.length}{" "}
            {row.activities.length === 1 ? "activity" : "activities"}
          </div>
        </>
      ),
    },
    {
      title: "Machinery Cost",
      dataIndex: "cost",
      width: 130,
      align: "right",
      render: costCell,
    },
    {
      title: "% of Project",
      dataIndex: "share",
      width: 150,
      render: shareCell,
    },
  ];

  const activityColumns: ColumnsType<ActivityRow> = [
    {
      title: "#",
      dataIndex: "rank",
      width: 48,
      render: (rank: number) => (
        <span style={{ color: "#8c8c8c" }}>{rank}</span>
      ),
    },
    {
      title: "Activity",
      dataIndex: "name",
      render: (name: string) => (
        <span style={{ fontWeight: 500 }} title={name}>
          {wbsName(name)}
        </span>
      ),
    },
    {
      title: "Machinery Cost",
      dataIndex: "cost",
      width: 130,
      align: "right",
      render: costCell,
    },
    {
      title: "% of Phase",
      dataIndex: "share",
      width: 150,
      render: shareCell,
    },
  ];

  const topPhase = rows[0];

  return (
    <Drawer
      title="Phase Machinery Costs"
      placement="right"
      width="min(900px, 100vw)"
      onClose={onClose}
      open={open}
      destroyOnClose
      {...(getContainer ? { getContainer } : {})}
    >
      {rows.length === 0 ? (
        <Empty description="No phase cost data for this project yet" />
      ) : (
        <>
          {topPhase && topPhase.cost > 0 ? (
            <div style={{ marginBottom: 12 }}>
              <Tag color="gold" style={{ fontSize: 12 }}>
                Highest: {wbsName(topPhase.name)} · {fmtINRCompact(topPhase.cost)} (
                {topPhase.share.toFixed(1)}% of project)
              </Tag>
            </div>
          ) : null}
          <Table<PhaseRow>
            className="customTable"
            rowKey="key"
            columns={phaseColumns}
            dataSource={rows}
            pagination={false}
            bordered
            size="small"
            expandable={{
              rowExpandable: (record) => record.activities.length > 0,
              expandedRowRender: (record) => (
                <Table<ActivityRow>
                  className="customTable"
                  rowKey="key"
                  columns={activityColumns}
                  dataSource={record.activities}
                  pagination={false}
                  bordered
                  size="small"
                />
              ),
            }}
          />
        </>
      )}
    </Drawer>
  );
};

export default PhaseCostDrawer;
